import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, X, ChevronUp, ChevronDown, Pencil, ArrowRight,
  Scissors, Film, Image as ImageIcon, LayoutGrid, Megaphone, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

export const CONTENT_TYPES = ['Video Series', 'Single Video', 'Livestream', 'Multi-Platform Campaign', 'Custom'];
export const SEGMENT_PRESETS = ['Intro', 'Main Content', 'Interview', 'Sponsor Segment', 'Outro', 'Custom Segment'];
export const OUTPUT_TYPES = [
  { type: 'longform', label: 'Long-form Video', icon: Film },
  { type: 'shortform', label: 'Short-form Clips', icon: Scissors },
  { type: 'thumbnail', label: 'Thumbnail', icon: ImageIcon },
  { type: 'image_post', label: 'Image Post', icon: ImageIcon },
  { type: 'carousel', label: 'Carousel', icon: LayoutGrid },
  { type: 'promo_post', label: 'Promotional Post', icon: Megaphone },
];
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export function blankTemplate() {
  return {
    id: uid(),
    name: '',
    description: '',
    content_type: 'Video Series',
    series_name: '',
    segments: [{ id: uid(), name: 'Intro' }, { id: uid(), name: 'Main Content' }, { id: uid(), name: 'Outro' }],
    outputs: [{ id: uid(), type: 'longform', label: 'Long-form Video', segment_ids: [] }],
    use_brand_kit_defaults: false,
    owner: 'you',
  };
}

export default function ProjectTemplateEditorDemo({ open, onOpenChange, template, onSave }) {
  const [draft, setDraft] = useState(blankTemplate());

  useEffect(() => {
    if (open) setDraft(template ? JSON.parse(JSON.stringify(template)) : blankTemplate());
  }, [open, template]);

  const addSegment = (preset) => setDraft(d => ({ ...d, segments: [...d.segments, { id: uid(), name: preset === 'Custom Segment' ? 'New Segment' : preset }] }));
  const renameSegment = (id, name) => setDraft(d => ({ ...d, segments: d.segments.map(s => s.id === id ? { ...s, name } : s) }));
  const removeSegment = (id) => setDraft(d => ({
    ...d,
    segments: d.segments.filter(s => s.id !== id),
    outputs: d.outputs.map(o => ({ ...o, segment_ids: o.segment_ids.filter(sid => sid !== id) })),
  }));
  const moveSegment = (idx, dir) => setDraft(d => {
    const arr = [...d.segments];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return d;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    return { ...d, segments: arr };
  });

  const addOutput = (type) => {
    const meta = OUTPUT_TYPES.find(o => o.type === type);
    setDraft(d => ({ ...d, outputs: [...d.outputs, { id: uid(), type, label: meta.label, segment_ids: [], ...(type === 'shortform' ? { target_clip_count: 3 } : {}) }] }));
  };
  const removeOutput = (id) => setDraft(d => ({ ...d, outputs: d.outputs.filter(o => o.id !== id) }));
  const updateOutput = (id, patch) => setDraft(d => ({ ...d, outputs: d.outputs.map(o => o.id === id ? { ...o, ...patch } : o) }));
  const toggleOutputSegment = (outputId, segId) => setDraft(d => ({
    ...d,
    outputs: d.outputs.map(o => o.id === outputId
      ? { ...o, segment_ids: o.segment_ids.includes(segId) ? o.segment_ids.filter(x => x !== segId) : [...o.segment_ids, segId] }
      : o),
  }));

  const handleSave = () => {
    if (!draft.name.trim()) { toast.error('Give this template a name first'); return; }
    onSave({ ...draft, updated_at: new Date().toISOString() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">{template ? 'Edit Template' : 'Create Template'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic information */}
          <section className="space-y-3">
            <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Basic Information</Label>
            <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Template name, e.g. Weekly Podcast Rollout" className="bg-white/5 border-white/10 text-white" data-testid="template-name-input" />
            <Textarea value={draft.description} onChange={e => setDraft(d => ({ ...d, description: e.target.value }))} placeholder="What's this template for?" className="bg-white/5 border-white/10 text-white resize-none" rows={2} data-testid="template-description-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={draft.content_type} onValueChange={v => setDraft(d => ({ ...d, content_type: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="template-content-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1C1F26] border-white/10">{CONTENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={draft.series_name} onChange={e => setDraft(d => ({ ...d, series_name: e.target.value }))} placeholder="Series name (optional)" className="bg-white/5 border-white/10 text-white" data-testid="template-series-name-input" />
            </div>
          </section>

          {/* Production segments */}
          <section className="space-y-3">
            <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Production Segments</Label>
            <div className="space-y-2" data-testid="template-segments-list">
              {draft.segments.map((seg, idx) => (
                <div key={seg.id} className="flex items-center gap-2 bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2">
                  <div className="flex flex-col -my-1">
                    <button type="button" onClick={() => moveSegment(idx, -1)} disabled={idx === 0} className="text-[#555E6E] hover:text-white disabled:opacity-30" data-testid={`segment-up-${seg.id}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveSegment(idx, 1)} disabled={idx === draft.segments.length - 1} className="text-[#555E6E] hover:text-white disabled:opacity-30" data-testid={`segment-down-${seg.id}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <Input value={seg.name} onChange={e => renameSegment(seg.id, e.target.value)} className="bg-transparent border-none text-white h-8 px-1 focus-visible:ring-1" data-testid={`segment-name-${seg.id}`} />
                  <button type="button" onClick={() => removeSegment(seg.id)} className="text-[#555E6E] hover:text-red-400 shrink-0" data-testid={`segment-remove-${seg.id}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SEGMENT_PRESETS.map(p => (
                <Button key={p} type="button" size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => addSegment(p)} data-testid={`add-segment-${p.replace(/\s+/g, '-').toLowerCase()}`}>
                  <Plus className="h-3 w-3 mr-1" />{p}
                </Button>
              ))}
            </div>
          </section>

          {/* Expected outputs */}
          <section className="space-y-3">
            <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Expected Outputs</Label>
            <div className="space-y-3" data-testid="template-outputs-list">
              {draft.outputs.map(o => {
                const Icon = OUTPUT_TYPES.find(t => t.type === o.type)?.icon || Film;
                return (
                  <div key={o.id} className="bg-white/[0.03] border border-white/5 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-sm text-white"><Icon className="h-3.5 w-3.5 text-[#3A7BFF]" />{o.label}</span>
                      <button type="button" onClick={() => removeOutput(o.id)} className="text-[#555E6E] hover:text-red-400" data-testid={`output-remove-${o.id}`}><X className="h-3.5 w-3.5" /></button>
                    </div>
                    {o.type === 'shortform' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-[#8A93A6]">Target clip count</Label>
                        <Input type="number" min={1} value={o.target_clip_count || 1} onChange={e => updateOutput(o.id, { target_clip_count: parseInt(e.target.value) || 1 })} className="bg-white/5 border-white/10 text-white h-7 w-20 text-xs" data-testid={`output-clip-count-${o.id}`} />
                      </div>
                    )}
                    {draft.segments.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {draft.segments.map(seg => (
                          <label key={seg.id} className="flex items-center gap-1.5 text-xs text-[#8A93A6] cursor-pointer">
                            <Checkbox checked={o.segment_ids.includes(seg.id)} onCheckedChange={() => toggleOutputSegment(o.id, seg.id)} />
                            {seg.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {OUTPUT_TYPES.map(t => (
                <Button key={t.type} type="button" size="sm" variant="outline" className="h-7 text-xs border-white/10" onClick={() => addOutput(t.type)} data-testid={`add-output-${t.type}`}>
                  <Plus className="h-3 w-3 mr-1" />{t.label}
                </Button>
              ))}
            </div>
          </section>

          {/* Production workflow preview */}
          <section className="space-y-2">
            <Label className="text-[#8A93A6] text-xs uppercase tracking-wider">Production Workflow</Label>
            <div className="flex items-center gap-1.5 flex-wrap text-xs text-[#8A93A6] bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5" data-testid="template-workflow-preview">
              {['Segments', 'Editing', 'Clips', 'Thumbnails/Images', 'Optimization', 'Scheduling'].map((step, i, arr) => (
                <React.Fragment key={step}>
                  <Badge variant="outline" className="border-white/10 text-[#C0C7D4]">{step}</Badge>
                  {i < arr.length - 1 && <ArrowRight className="h-3 w-3 text-[#555E6E]" />}
                </React.Fragment>
              ))}
            </div>
            <p className="text-[11px] text-[#555E6E]">This is a reusable production blueprint, not a media editor — no real processing happens here.</p>
          </section>

          {/* Brand Kit */}
          <section className="flex items-center justify-between bg-white/[0.03] border border-white/5 rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-400" />
              <div>
                <p className="text-sm text-white">Use my Brand Kit defaults</p>
                <p className="text-[11px] text-[#555E6E]">Simulated for now — real Brand Kit application will be connected later.</p>
              </div>
            </div>
            <Switch checked={draft.use_brand_kit_defaults} onCheckedChange={v => setDraft(d => ({ ...d, use_brand_kit_defaults: v }))} data-testid="template-brand-kit-switch" />
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" className="border-white/10" onClick={() => onOpenChange(false)} data-testid="template-editor-cancel-btn">Cancel</Button>
          <Button className="bg-[#3A7BFF] hover:bg-[#3A7BFF]/80" onClick={handleSave} data-testid="template-editor-save-btn"><Pencil className="h-4 w-4 mr-2" />Save Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
